<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
// Overrides @basecom-gmbh/pond/components/Layout/Header/LayoutHeaderCart.vue
// IDS-160: use custom vendor hint component
const offcanvasCartController = useModal();
const { isEmpty } = useCart();
const cartItemsStore = useCartItemsStore();
const { cartItemsCount } = storeToRefs(cartItemsStore);
// CUSTOM IDS-79
const { groupedCartItems, getShippingMethodCost } = useGroupedCartItems();
const { url } = useCountrySwitch();

// IDS-177 - trigger to modal through 'outside' state change
const openState = useState('cartOpen', () => false);

watch(openState, value => {
    if (value) {
        offcanvasCartController.open();
    } else {
        offcanvasCartController.close();
    }
});

watch(offcanvasCartController.isOpen, value => {
    openState.value = value;
});
</script>

<template>
    
<FormKitLazyProvider config-file="true">
<button
        class="relative"
        @click="offcanvasCartController.open()"
    >
        <FormKitIcon
            class="block w-4.5 *:fill-none"
            icon="cart-shopping"
        />
        <UtilityPill
            :number="cartItemsCount"
            class="absolute bottom-2.5 left-3"
        />
    </button>

    <ClientOnly>
        <LazyLayoutSidebar
            v-if="offcanvasCartController.isOpen"
            :controller="offcanvasCartController"
            class="h-full flex-grow"
            side="right"
        >
            <template #header>
                <div>
                    <h1 class="mr-4 inline-block font-extrabold leading-8">{{ $t('checkout.cart.heading') }}</h1>
                    <span
                        v-if="cartItemsCount === 0 || cartItemsCount > 1"
                        class="text-sm"
                        >{{ $t('checkout.cart.headingProductCount', { count: cartItemsCount }) }}</span
                    >
                    <span
                        v-if="cartItemsCount === 1"
                        class="text-sm"
                        >{{ $t('checkout.cart.headingProductCountOne') }}</span
                    >
                </div>
            </template>
            <template #content>
                <div class="h-full flex-grow px-5">
                    <div v-if="!isEmpty">
                        <ul>
                            <!-- CUSTOM IDS-79 Grouped Items by Vendor. added fees and link fields as well-->
                            <div
                                v-for="(group, vendorId) in groupedCartItems"
                                :key="vendorId"
                                class="mb-8"
                            >
                                <h2 class="mb-1 text-sm font-bold">
                                    {{ $t('checkout.cart.soldByVendor') }}

                                    <SharedVendorHint
                                        v-if="group.vendor"
                                        :vendor="group.vendor"
                                    />
                                </h2>
                                <span class="text-sm text-neutral-600">
                                    {{
                                        $t('checkout.cart.estimatedDelivery', {
                                            time: getShippingMethodCost(group.vendor?.id).shippingDeliveryTime,
                                        })
                                    }}
                                </span>
                                <li
                                    v-for="item in group.items"
                                    :key="item.cartItem.id"
                                    class="flex border-b border-neutral-500 py-6"
                                >
                                    <CheckoutLineItem
                                        :line-item="item.cartItem"
                                        :product="item.product"
                                        :mini-cart="true"
                                    />
                                </li>
                                <CheckoutVendorFees
                                    :vendor="group.vendor"
                                    :mini-cart="true"
                                />
                            </div>
                        </ul>
                        <CheckoutSummary :reduced-display="true" />
                    </div>
                    <div
                        v-else
                        class="flex h-full flex-grow flex-col items-center justify-center"
                    >
                        <img
                            src="/assets/icons/ids_empty_cart.svg"
                            :alt="$t('checkout.cart.altEmptyCart')"
                        />
                        <span class="non-italic text-lg font-extrabold leading-6">{{
                            $t('checkout.cart.emptyCartMessage')
                        }}</span>
                    </div>
                </div>
            </template>
            <template #footer>
                <NuxtLink
                    v-if="!isEmpty"
                    :to="url('/checkout/cart')"
                    class="text-white mx-5 my-4 flex items-center justify-center rounded-full bg-neutral-800 px-6 py-3 font-extrabold"
                    @click="offcanvasCartController.close()"
                >
                    {{ $t('checkout.offcanvasCart.toCartButtonLabel') }}
                </NuxtLink>
            </template>
        </LazyLayoutSidebar>
    </ClientOnly>
</FormKitLazyProvider>

</template>
